import {
  Collapse,
  Dropdown,
  InputSearch,
  SkeletonContent,
  Button,
} from "@endya-conformite/ui-kit";
import CheckIcon from "@endya-conformite/ui-kit/src/assets/icons/check.svg?react";
import CloseIcon from "@endya-conformite/ui-kit/src/assets/icons/x-lg.svg?react";
import FilterIcon from "@endya-conformite/ui-kit/src/assets/icons/filter.svg?react";
import { PersonnesAVerifierProps } from "@src/components/sidebar/sidebar.types";
import styles from "@src/components/sidebar/SidebarDroite/SidebarDroite.module.scss";
import { useCQPersonnesAssujettiesQuery } from "@src/data/controle-quinquennal/hook/useCQPersonnesAssujettiesQuery";
import { useEntreprise } from "@src/data/entreprise/hook/useEntreprise";
import { getAvailableAxes } from "@src/helper/avancement.helper";
import { PersonneVerificationStatus } from "@src/helper/remediation.helper";
import { ExportAssujettisListButton } from "@src/pages/controle-quinquennal/content/ExportAssujettisListButton";
import { usePersonnesVerificationStatus } from "@src/pages/controle-quinquennal/usePersonnesVerificationStatus";
import classNames from "classnames";
import { useEffect, useMemo, useState } from "react";
import { SIDEBAR_CONSTANTS } from "./sidebar-constants";

const { PERSONNE_LIMIT, SEARCH_DEBOUNCE_MS } = SIDEBAR_CONSTANTS;

const PERSONNE_STATUS_CLASS: Record<
  PersonneVerificationStatus,
  string | undefined
> = {
  NON_VERIFIE: undefined,
  EN_COURS: styles.statusEnCours,
  VERT: styles.statusVert,
  ROUGE: styles.statusRouge,
  ORANGE: styles.statusOrange,
};

const STATUS_FILTER_OPTIONS: {
  value: PersonneVerificationStatus;
  label: string;
  dotClass: string;
}[] = [
  { value: "NON_VERIFIE", label: "À traiter", dotClass: styles.dotNonVerifie },
  { value: "EN_COURS", label: "En cours", dotClass: styles.dotEnCours },
  { value: "VERT", label: "Conforme", dotClass: styles.dotVert },
  { value: "ROUGE", label: "Remédiation", dotClass: styles.dotRouge },
  { value: "ORANGE", label: "Manquant", dotClass: styles.dotOrange },
];

type StatusFilterMenuProps = {
  applied: PersonneVerificationStatus[];
  onApply: (next: PersonneVerificationStatus[]) => void;
  close: () => void;
};

function StatusFilterMenu({ applied, onApply, close }: StatusFilterMenuProps) {
  const [draft, setDraft] = useState<PersonneVerificationStatus[]>(applied);

  const toggle = (value: PersonneVerificationStatus) => {
    setDraft((prev) =>
      prev.includes(value)
        ? prev.filter((status) => status !== value)
        : [...prev, value]
    );
  };

  return (
    <div className={styles.statusFilterMenu}>
      {STATUS_FILTER_OPTIONS.map((option) => {
        const checked = draft.includes(option.value);
        return (
          <button
            key={option.value}
            type="button"
            className={styles.statusFilterOption}
            onClick={() => toggle(option.value)}
            aria-pressed={checked}
          >
            <span
              className={classNames(styles.checkbox, {
                [styles.checkboxChecked]: checked,
              })}
            >
              {checked && <CheckIcon className={styles.checkIcon} />}
            </span>
            <span
              className={classNames(styles.statusFilterDot, option.dotClass)}
            />
            {option.label}
          </button>
        );
      })}
      <div className={styles.statusFilterFooter}>
        <Button
          variant="text"
          onClick={() => {
            onApply(draft);
            close();
          }}
        >
          Appliquer
        </Button>
      </div>
    </div>
  );
}

export function PersonnesAVerifier({
  idEntreprise,
  siren,
  millesime,
  onPersonneSelected,
  selectedPersonneId,
}: PersonnesAVerifierProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [displayedCount, setDisplayedCount] = useState<number>(PERSONNE_LIMIT);
  const [appliedStatusFilters, setAppliedStatusFilters] = useState<
    PersonneVerificationStatus[]
  >([]);

  const { data: assujettisResponse, isLoading: assujettisIsLoading } =
    useCQPersonnesAssujettiesQuery(
      millesime,
      siren,
      debouncedSearch,
      displayedCount
    );

  const { data: totalResponse } = useCQPersonnesAssujettiesQuery(
    millesime,
    siren,
    "",
    PERSONNE_LIMIT
  );

  const { data: entreprise } = useEntreprise(millesime, idEntreprise);

  const personneIds = useMemo(
    () => assujettisResponse?.assujettis.map((personne) => personne.id) ?? [],
    [assujettisResponse]
  );

  const statusByPersonne = usePersonnesVerificationStatus(
    personneIds,
    entreprise
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setDisplayedCount(PERSONNE_LIMIT);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const handlePersonneSelected = (personneId: string) => {
    onPersonneSelected(personneId);
  };

  if (assujettisIsLoading || !assujettisResponse)
    return (
      <div className={styles.filesContainer}>
        <SkeletonContent />
      </div>
    );

  const { assujettis, total } = assujettisResponse;
  const totalInitial = totalResponse?.total ?? total;

  const filteredAssujettis =
    appliedStatusFilters.length === 0
      ? assujettis
      : assujettis.filter((personne) =>
          appliedStatusFilters.includes(
            statusByPersonne[personne.id] ?? "NON_VERIFIE"
          )
        );

  const activeFilterLabel =
    appliedStatusFilters.length > 0
      ? `Filtrer (${appliedStatusFilters.length})`
      : "Filtrer";

  return (
    <Collapse
      isOpen={isOpen}
      title={`Personnes à vérifier (${totalInitial})`}
      onToggle={() => setIsOpen((prevState) => !prevState)}
      hasToggleText={false}
    >
      {assujettis.length > 0 && (
        <div className={styles.personnesHeader}>
          <small>
            ({filteredAssujettis.length}{" "}
            {filteredAssujettis.length > 1 ? "affichées" : "affichée"} sur{" "}
            {totalInitial})
          </small>
          <div className={styles.filterControls}>
            <Dropdown
              variant="text"
              placement="bottom-end"
              dismissOnClick
              icon={<FilterIcon />}
              content={activeFilterLabel}
              className={styles.statusFilterTrigger}
            >
              {({ close }) => (
                <StatusFilterMenu
                  applied={appliedStatusFilters}
                  onApply={setAppliedStatusFilters}
                  close={close}
                />
              )}
            </Dropdown>
            {appliedStatusFilters.length > 0 && (
              <button
                type="button"
                className={styles.filterReset}
                onClick={() => setAppliedStatusFilters([])}
                aria-label="Réinitialiser les filtres"
              >
                <CloseIcon className={styles.filterResetIcon} />
              </button>
            )}
            <ExportAssujettisListButton
              availableAxes={getAvailableAxes(entreprise)}
              siren={siren}
              millesime={millesime}
            />
          </div>
        </div>
      )}
      <div className={classNames(styles.filesContainer, styles.scroll)}>
        {filteredAssujettis.map((personne) => (
          <div
            key={personne.id}
            className={classNames(
              styles.personneCard,
              PERSONNE_STATUS_CLASS[
                statusByPersonne[personne.id] ?? "NON_VERIFIE"
              ],
              {
                [styles.selected]: selectedPersonneId === personne.id,
              }
            )}
            role="button"
            onClick={() => {
              handlePersonneSelected(personne.id);
            }}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handlePersonneSelected(personne.id);
              }
            }}
          >
            {personne.surname} {personne.name}
          </div>
        ))}
        {assujettis.length > 0 && filteredAssujettis.length === 0 && (
          <small>Aucune personne pour cet état</small>
        )}
        {assujettis.length < total && (
          <Button
            onClick={() => setDisplayedCount((prev) => prev + PERSONNE_LIMIT)}
            variant="text"
            className={styles.download}
          >
            {`Charger plus (
            ${assujettis.length}/${total}
            )`}
          </Button>
        )}
        {assujettis.length === 0 && (
          <div>Aucune personne assujettie trouvée</div>
        )}
      </div>
      <InputSearch
        name="search"
        placeholder="Rechercher une personne"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
      />
    </Collapse>
  );
}
